import { triggerAuthError } from '../auth/authErrorHandler';
import { createServerError } from './apiError';
import { invalidateItemsCacheForSlug } from './itemsApi';
import { resolveApiBaseUrl } from './apiBase';

const API_BASE_URL = resolveApiBaseUrl();

function appendOrganizationQuery(endpoint: string, organization?: string | null): string {
    const o = typeof organization === 'string' ? organization.trim() : '';
    if (!o) return endpoint;
    const sep = endpoint.includes('?') ? '&' : '?';
    return `${endpoint}${sep}organization=${encodeURIComponent(o)}`;
}

export interface ApiResponse<T = any> {
    success: boolean;
    data?: T;
    message?: string;
    total?: number;
    /** Legacy shape from some collection/component endpoints */
    components?: any[];
}

export interface ActivityLogEntry {
    uid: string;
    collectionSlug: string;
    collectionUid?: string;
    action: string;
    itemId?: string;
    itemSummary?: string;
    userUid?: string;
    userEmail?: string;
    count?: number;
    changes?: Array<{
        field: string;
        label: string;
        before: string | null;
        after: string | null;
    }>;
    date: string;
}

class DataAPI {
    private getAuthToken = (): string | null => {
        const storages = [localStorage, sessionStorage];
        for (const storage of storages) {
            const authToken = storage.getItem('authToken');
            if (authToken && authToken.trim()) {
                return authToken.trim();
            }
        }
        for (const storage of storages) {
            const legacyToken = storage.getItem('idToken');
            if (legacyToken && legacyToken.trim()) {
                return legacyToken.trim();
            }
        }
        return null;
    };

    // Token refresh is not available - endpoint doesn't exist
    // Keeping method for potential future use, but it always returns null
    private refreshToken = async (): Promise<string | null> => {
        // Refresh endpoint doesn't exist - return null immediately
        // This prevents unnecessary network calls and confusion
        console.log('⚠️ DataAPI: Token refresh endpoint not available - skipping refresh attempt');
        return null;
    };

    private makeRequest = async (endpoint: string, options: RequestInit = {}, retryOn401: boolean = true): Promise<any> => {
        const token = this.getAuthToken();

        // Log token info for debugging (masked)
        if (token) {
            const tokenPreview = token.length > 20 ? `${token.substring(0, 10)}...${token.substring(token.length - 10)}` : '***';
            console.log(`🔑 DataAPI: Making request to ${endpoint} with token: ${tokenPreview} (length: ${token.length})`);
        } else {
            console.log(`⚠️ DataAPI: Making request to ${endpoint} without token`);
        }

        const config: RequestInit = {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...options.headers,
            },
        };

        if (token) {
            const authHeader = `Bearer ${token}`;
            config.headers = {
                ...config.headers,
                'Authorization': authHeader,
            };
            // Log the actual header being sent (first 30 chars only for security)
            console.log(`🔑 DataAPI: Authorization header: ${authHeader.substring(0, 30)}...`);
        }

        const fullUrl = `${API_BASE_URL}${endpoint}`;
        console.log(`🌐 DataAPI: Making ${options.method || 'GET'} request to ${fullUrl}`);

        const response = await fetch(fullUrl, config);

        // Log response status
        console.log(`📡 DataAPI: Response status: ${response.status} ${response.statusText} for ${endpoint}`);

        if (!response.ok) {
            if (response.status >= 500) {
                throw createServerError(response.status);
            }
            let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
            let errorData: any = null;

            try {
                errorData = await response.json();
                errorMessage = errorData.message || errorData.error || errorMessage;
            } catch (e) {
                // If response is not JSON, use status text
                const text = await response.text().catch(() => '');
                errorMessage = text || errorMessage;
            }

            // Handle 400 Bad Request specifically
            if (response.status === 400) {
                const detailedMessage = errorData?.message || errorData?.error || errorMessage;
                let fullMessage = `HTTP ${response.status} ${response.statusText}: ${detailedMessage}`;

                // Include validation errors if present
                if (errorData?.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
                    const errorsList = errorData.errors.map((err: any) => {
                        if (typeof err === 'string') return err;
                        if (err?.message) return err.message;
                        if (err?.field && err?.message) return `${err.field}: ${err.message}`;
                        return JSON.stringify(err);
                    }).join('; ');
                    fullMessage += `\nValidation errors: ${errorsList}`;
                }

                console.error('❌ DataAPI: Bad Request (400) for', endpoint);
                console.error('❌ DataAPI: Error details:', errorData);
                console.error('❌ DataAPI: Full error message:', fullMessage);
                throw new Error(fullMessage);
            }

            // Handle 403 Forbidden (Permission Denied)
            if (response.status === 403) {
                const detailedMessage = errorData?.message || errorData?.error || errorMessage;
                console.error('⛔ DataAPI: Permission Denied (403)');
                console.error('⛔ DataAPI: Endpoint:', endpoint);
                console.error('⛔ DataAPI: Error message:', detailedMessage);
                console.error('⛔ DataAPI: You may need to create an ACL permission');
                console.error('⛔ DataAPI: Check backend logs for detailed permission requirements');

                errorMessage = 'Permission denied. Access is not configured. Please contact an administrator.';
                throw new Error(errorMessage);
            }

            // Handle 401 errors: token invalid/expired (e.g. after server restart or clock skew)
            if (response.status === 401) {
                console.error('❌ DataAPI: Received 401 Unauthorized for', endpoint);
                const clearAuthStorage = () => {
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('idToken');
                    localStorage.removeItem('user');
                    sessionStorage.removeItem('authToken');
                    sessionStorage.removeItem('idToken');
                };
                clearAuthStorage();
                triggerAuthError();
                errorMessage = errorData?.message || 'Authentication required. Please log in again.';
                throw new Error(errorMessage);
            }

            // Provide helpful error messages for common issues
            if (response.status === 404) {
                errorMessage = `Endpoint not found: ${endpoint}. Make sure the API server is running and the endpoint exists.`;
            }

            const fullErrorMessage = `HTTP ${response.status} ${response.statusText}: ${errorMessage}`;
            console.error(`❌ DataAPI: Request failed for ${endpoint}:`, fullErrorMessage);
            throw new Error(fullErrorMessage);
        }

        return await response.json();
    };

    // Collection operations
    // Uses slug (e.g., 'bookings', 'inventory') to identify the component
    // The backend resolves the slug to the appropriate collection based on component configuration
    async getCollectionItems(slug: string, params?: {
        page?: number;
        limit?: number;
        offset?: number;
        sort?: string;
        orderBy?: string;
        orderDirection?: 'asc' | 'desc';
        filter?: any;
        organization?: string;
    }): Promise<ApiResponse> {
        const queryParams = new URLSearchParams();

        // Support new API parameters: limit, offset, orderBy, orderDirection
        if (params?.limit) queryParams.append('limit', params.limit.toString());
        if (params?.offset !== undefined) queryParams.append('offset', params.offset.toString());
        if (params?.orderBy) queryParams.append('orderBy', params.orderBy);
        if (params?.orderDirection) queryParams.append('orderDirection', params.orderDirection);

        // Legacy support: convert page to offset if provided
        if (params?.page && !params?.offset) {
            const limit = params.limit || 10;
            queryParams.append('offset', ((params.page - 1) * limit).toString());
        }

        // Legacy support: convert sort to orderBy/orderDirection if provided
        if (params?.sort && !params?.orderBy) {
            // Sort format: "field:asc" or "field:desc" or just "field"
            const sortParts = params.sort.split(':');
            queryParams.append('orderBy', sortParts[0]);
            if (sortParts[1]) {
                queryParams.append('orderDirection', sortParts[1]);
            }
        }

        // Note: filter parameter not supported in new API - would need to be handled server-side
        // if (params?.filter) queryParams.append('filter', JSON.stringify(params.filter));

        if (params?.organization && String(params.organization).trim()) {
            queryParams.set('organization', String(params.organization).trim());
        }
        const queryString = queryParams.toString();
        const endpoint = `/collections/${slug}/items${queryString ? `?${queryString}` : ''}`;

        const response = await this.makeRequest(endpoint);

        // Transform response from { success, items, count, component, dataSource }
        // to { success, data, total } for backward compatibility
        if (response.items) {
            return {
                success: response.success,
                data: response.items,
                total: response.count,
                message: response.message
            };
        }

        return response;
    }

    // Uses slug (e.g., 'bookings', 'inventory') to identify the component
    // The backend resolves the slug to the appropriate collection based on component configuration
    async getCollectionItem(slug: string, uid: string, organization?: string | null): Promise<ApiResponse> {
        const path = appendOrganizationQuery(`/collections/${slug}/items/${uid}`, organization);
        const response = await this.makeRequest(path);

        // Transform response from { success, item, dataSource }
        // to { success, data } for backward compatibility
        if (response.item) {
            return {
                success: response.success,
                data: response.item,
                message: response.message,
                followUpNotifications: response.followUpNotifications
            };
        }

        return response;
    }

    // Uses slug (e.g., 'bookings', 'inventory') to identify the component
    // The backend resolves the slug to the appropriate collection based on component configuration
    async createCollectionItem(slug: string, data: any, organization?: string | null): Promise<ApiResponse> {
        const path = appendOrganizationQuery(`/collections/${slug}/items`, organization);
        const response = await this.makeRequest(path, {
            method: 'POST',
            body: JSON.stringify(data),
        });

        invalidateItemsCacheForSlug(slug, organization);
        // Transform response from { success, message, item, dataSource }
        // to { success, data } for backward compatibility
        if (response.item) {
            return {
                success: response.success,
                data: response.item,
                message: response.message,
                followUpNotifications: response.followUpNotifications
            };
        }

        return response;
    }

    // Update a collection item via PUT request
    // Uses slug (e.g., 'bookings', 'inventory') to identify the component
    // The backend resolves the slug to the appropriate collection based on component configuration
    // Note: PUT requests should use 'objectId' for the identifier (passed from itemData.ts)
    // The parameter accepts either objectId or uid (as fallback)
    async updateCollectionItem(slug: string, identifier: string, data: any, organization?: string | null): Promise<ApiResponse> {
        const path = appendOrganizationQuery(`/collections/${slug}/items/${identifier}`, organization);
        const response = await this.makeRequest(path, {
            method: 'PUT',
            body: JSON.stringify(data),
        });

        invalidateItemsCacheForSlug(slug);
        // Transform response from { success, message, item, dataSource }
        // to { success, data } for backward compatibility
        if (response.item) {
            return {
                success: response.success,
                data: response.item,
                message: response.message,
                followUpNotifications: response.followUpNotifications
            };
        }

        return response;
    }

    // Generate a new code for a code-type field (for use in item editor).
    async generateCode(slug: string, fieldName: string, organization?: string | null): Promise<ApiResponse<{ code: string }>> {
        const path = appendOrganizationQuery(`/collections/${slug}/items/generate-code`, organization);
        const response = await this.makeRequest(path, {
            method: 'POST',
            body: JSON.stringify({ fieldName }),
        });
        if (response.success && response.code != null) {
            return { success: true, data: { code: response.code } };
        }
        return response as ApiResponse<{ code: string }>;
    }

    // Uses slug (e.g., 'bookings', 'inventory') to identify the component
    // The backend resolves the slug to the appropriate collection based on component configuration
    async deleteCollectionItem(slug: string, uid: string, organization?: string | null): Promise<ApiResponse> {
        const path = appendOrganizationQuery(`/collections/${slug}/items/${uid}`, organization);
        const result = await this.makeRequest(path, {
            method: 'DELETE',
        });
        invalidateItemsCacheForSlug(slug);
        return result;
    }

    async deleteCollectionItemsBulk(slug: string, uids: string[], organization?: string | null): Promise<ApiResponse> {
        const path = appendOrganizationQuery(`/collections/${slug}/items/bulk-delete`, organization);
        const result = await this.makeRequest(path, {
            method: 'POST',
            body: JSON.stringify({ ids: uids }),
        });
        invalidateItemsCacheForSlug(slug);
        return result;
    }

    async getCollectionActivityLog(
        slug: string,
        params?: { action?: string; orderBy?: 'date' | 'action'; orderDirection?: 'asc' | 'desc'; limit?: number; offset?: number; organization?: string }
    ): Promise<ApiResponse<{ entries: ActivityLogEntry[]; total: number }>> {
        const searchParams = new URLSearchParams();
        if (params?.action) searchParams.set('action', params.action);
        if (params?.orderBy) searchParams.set('orderBy', params.orderBy);
        if (params?.orderDirection) searchParams.set('orderDirection', params.orderDirection);
        if (params?.limit != null) searchParams.set('limit', String(params.limit));
        if (params?.offset != null) searchParams.set('offset', String(params.offset));
        if (params?.organization && String(params.organization).trim()) {
            searchParams.set('organization', String(params.organization).trim());
        }
        const qs = searchParams.toString();
        const url = `/collections/${slug}/activity-log${qs ? `?${qs}` : ''}`;
        const response = await this.makeRequest(url);
        if (response.entries && typeof response.total === 'number') {
            return { success: response.success, data: { entries: response.entries, total: response.total }, total: response.total };
        }
        return response as ApiResponse<{ entries: ActivityLogEntry[]; total: number }>;
    }

    async deleteComponent(uid: string): Promise<ApiResponse> {
        return await this.makeRequest(`/collections/components/${uid}`, {
            method: 'DELETE',
        });
    }

    async listActions(): Promise<ApiResponse> {
        const response = await this.makeRequest('/actions');
        if (response.actions) {
            return {
                success: response.success,
                data: response.actions,
                total: response.count,
                message: response.message
            };
        }
        return response;
    }

    async createAction(data: any): Promise<ApiResponse> {
        const response = await this.makeRequest('/actions', {
            method: 'POST',
            body: JSON.stringify(data),
        });
        if (response.action) {
            return {
                success: response.success,
                data: response.action,
                message: response.message
            };
        }
        return response;
    }

    async updateAction(uid: string, data: any): Promise<ApiResponse> {
        const response = await this.makeRequest(`/actions/${uid}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
        if (response.action) {
            return {
                success: response.success,
                data: response.action,
                message: response.message
            };
        }
        return response;
    }

    async deleteAction(uid: string): Promise<ApiResponse> {
        return await this.makeRequest(`/actions/${uid}`, {
            method: 'DELETE',
        });
    }

    // Uses slug (e.g., 'bookings', 'inventory') to identify the component
    // The backend resolves the slug to the appropriate collection based on component configuration
    async quickUpdateCollectionItem(slug: string, uid: string, data: any, organization?: string | null): Promise<ApiResponse> {
        const path = appendOrganizationQuery(`/collections/${slug}/items/${uid}/quick-update`, organization);
        const response = await this.makeRequest(path, {
            method: 'PUT',
            body: JSON.stringify(data),
        });

        // Transform response from { success, message, item, dataSource }
        // to { success, data } for backward compatibility
        if (response.item) {
            return {
                success: response.success,
                data: response.item,
                message: response.message,
                followUpNotifications: response.followUpNotifications
            };
        }

        return response;
    }

    // Users list for ACL and admin (uses /users/peers; admins get all active users)
    async getUsersList(): Promise<ApiResponse & { peers?: any[] }> {
        return await this.makeRequest('/users/peers');
    }

    // Component operations
    async getComponents(): Promise<ApiResponse> {
        console.log('📡 DataAPI: Requesting components from /collections/components');
        const result = await this.makeRequest('/collections/components');
        console.log('📡 DataAPI: Components response:', result);
        return result;
    }

    async suggestUniqueCollectionName(baseName: string, excludeUid?: string | null): Promise<ApiResponse<{ suggestedName: string }>> {
        const params = new URLSearchParams({ base: baseName });
        if (excludeUid != null && excludeUid !== '') {
            params.set('excludeUid', excludeUid);
        }
        return await this.makeRequest(`/collections/suggest-unique-name?${params.toString()}`, {
            method: 'GET',
        });
    }

    async createComponent(data: any): Promise<ApiResponse> {
        const raw = (await this.makeRequest('/collections/create', {
            method: 'POST',
            body: JSON.stringify({ component: data }),
        })) as ApiResponse & { component?: Record<string, unknown> };
        // Backend returns { success, component, storage, ... }; normalize so `data` is always the created component
        const created = raw?.data ?? raw?.component;
        if (created && typeof created === 'object' && !raw.data) {
            return { ...raw, data: created };
        }
        return raw;
    }

    async updateComponent(uid: string, data: any): Promise<ApiResponse> {
        return await this.makeRequest(`/collections/components/${uid}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    // File operations
    async uploadFile(file: File, metadata?: any): Promise<ApiResponse> {
        const formData = new FormData();
        formData.append('file', file);
        if (metadata) {
            formData.append('metadata', JSON.stringify(metadata));
        }

        const token = this.getAuthToken();
        const response = await fetch(`${API_BASE_URL}/files/upload`, {
            method: 'POST',
            headers: {
                ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            },
            body: formData,
        });

        if (response.status === 401) {
            localStorage.removeItem('idToken');
            localStorage.removeItem('user');
            triggerAuthError();
            throw new Error('Authentication required. Please login again.');
        }

        if (!response.ok) {
            if (response.status >= 500) {
                throw createServerError(response.status);
            }
            if (response.status === 413) {
                throw new Error('This file is too large to upload.');
            }
            const errorData = await response.json().catch(() => ({ message: 'Network error' }));
            throw new Error(errorData.message || 'Error uploading file');
        }

        return await response.json();
    }

    async uploadMultipleFiles(files: File[], metadata?: any): Promise<ApiResponse> {
        const formData = new FormData();
        files.forEach(file => formData.append('files', file));
        if (metadata) {
            formData.append('metadata', JSON.stringify(metadata));
        }

        const token = this.getAuthToken();
        const response = await fetch(`${API_BASE_URL}/files/upload/multiple`, {
            method: 'POST',
            headers: {
                ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            },
            body: formData,
        });

        if (response.status === 401) {
            localStorage.removeItem('idToken');
            localStorage.removeItem('user');
            triggerAuthError();
            throw new Error('Authentication required. Please login again.');
        }

        if (!response.ok) {
            if (response.status >= 500) {
                throw createServerError(response.status);
            }
            if (response.status === 413) {
                throw new Error('One or more files are too large to upload.');
            }
            const errorData = await response.json().catch(() => ({ message: 'Network error' }));
            throw new Error(errorData.message || 'Error uploading files');
        }

        return await response.json();
    }

    async getFile(fileId: string): Promise<ApiResponse> {
        return await this.makeRequest(`/files/${fileId}`);
    }

    async deleteFile(fileId: string): Promise<ApiResponse> {
        return await this.makeRequest(`/files/${fileId}`, {
            method: 'DELETE',
        });
    }

    async updateFileRelations(fileId: string, relations: any): Promise<ApiResponse> {
        return await this.makeRequest(`/files/${fileId}/relations`, {
            method: 'PUT',
            body: JSON.stringify(relations),
        });
    }

    // Sync operations
    async syncCompare(): Promise<ApiResponse> {
        return await this.makeRequest('/collections/sync/compare');
    }

    async syncMarkDeleted(data: any): Promise<ApiResponse> {
        return await this.makeRequest('/collections/sync/mark-deleted', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async syncFixCollections(): Promise<ApiResponse> {
        return await this.makeRequest('/collections/sync/fix-collections', {
            method: 'POST',
        });
    }

    async syncFull(): Promise<ApiResponse> {
        return await this.makeRequest('/collections/sync/full', {
            method: 'POST',
        });
    }

    async syncComponent(uid: string): Promise<ApiResponse> {
        return await this.makeRequest(`/collections/sync/component/${uid}`, {
            method: 'POST',
        });
    }
}

export const dataAPI = new DataAPI();
